import React, { useState, type FC } from 'react';
import cx from 'classnames';
import { type FormInstance } from 'antd';
import { InputText, Select, Space, Switchbox, Text } from '@g2u/ui-kit';
import { SearchOutlined } from '@ant-design/icons';

import { type JsonData, type JsonDataItem, type WithOptionalDataTest } from 'types';
import { AbnLookup, CustomerSearchModal, InputEmail, InputName, InputPhone, Section } from 'components';
import { useDynamicRequiredRules } from './hooks/useDynamicRequireRules';
import { useText } from 'helpers/hooks/useText';
import { validateFormSilently } from 'helpers';
import styles from './primary-contact-fields.module.scss';

export namespace PrimaryContactFields {
  export type Props = {
    form?: FormInstance;
    inline?: boolean;
    customer: JsonDataItem | null;
    hasScript?: boolean;
    initialValues?: JsonDataItem;
    isBusinessAccount?: boolean;
  } & WithOptionalDataTest;

  export const $: FC<Props> = (props) => {
    const { inline, customer, form, initialValues, hasScript, isBusinessAccount } = props;
    const [showCustomerSearchModal, setShowCustomerSearchModal] = useState(false);
    const [billToOptions, setBillToOptions] = useState<JsonData>(
      customer?.billto_ID
        ? [{ label: `${customer.billto_ID} - ${customer.billto_name}`, value: customer.billto_ID }]
        : [],
    );
    const { getText } = useText();
    const { isMobileRequired, isEmailRequired } = useDynamicRequiredRules(form);

    return (
      <Section.$ title="Primary Contact" inline={inline}>
        <Space.$
          direction={Space.Direction.Vertical}
          size={Space.Size.Middle}
          className={styles.primaryContact__contact}
        >
          {hasScript && <Text.$ className="script-box">{getText('booking.location.createCustomerFile')}</Text.$>}
          <Space.$
            direction={Space.Direction.Vertical}
            size={Space.Size.Small}
            className={styles.primaryContact__contactForm}
          >
            <div className={styles.primaryContact__formRow}>
              <Select.$
                name={'title'}
                label="Title:"
                fullWidth
                initialValue={customer?.name.contact1_title ? customer?.name.contact1_title : undefined}
                options={[
                  { label: '', value: '' },
                  { label: 'Mr', value: 'Mr' },
                  { label: 'Mrs', value: 'Mrs' },
                  { label: 'Ms', value: 'Ms' },
                  { label: 'Miss', value: 'Miss' },
                  { label: 'Dr', value: 'Dr' },
                  { label: 'Sir', value: 'Sir' },
                  { label: 'Lady', value: 'Lady' },
                  { label: 'Attn', value: 'Attn' },
                  { label: 'Snr Cons', value: 'Snr Cons' },
                ]}
                className={cx(styles.primaryContact__formItem_vertical, styles.primaryContact__contactFormItem)}
                dataTestId="title"
                placeholder="Select"
              />
              <InputName.$
                label="First Name:"
                dataTestId="first-name"
                name="firstName"
                className={cx(styles.primaryContact__formItem_vertical, styles.primaryContact__contactFormItem)}
                initialValue={initialValues?.firstName || customer?.name.contact1_firstname}
              />
            </div>
            <div className={styles.primaryContact__formRow}>
              <InputName.$
                label="Preferred Name:"
                dataTestId="preferred-name"
                name="preferredName"
                className={cx(styles.primaryContact__formItem_vertical, styles.primaryContact__contactFormItem)}
                initialValue={customer?.name.preferred_name}
                required={false}
              />
              <InputName.$
                label="Last Name:"
                dataTestId="last-name"
                name="lastName"
                className={cx(styles.primaryContact__formItem_vertical, styles.primaryContact__contactFormItem)}
                initialValue={initialValues?.lastName || customer?.name.contact1_surname}
              />
            </div>
            <div className={styles.primaryContact__formRow}>
              <InputEmail.$
                className={cx(styles.primaryContact__formItem_vertical, styles.primaryContact__contactFormItem)}
                initialValue={initialValues?.email || customer?.email}
                required={isEmailRequired}
                isBusiness={isBusinessAccount}
              />
              <InputPhone.$
                label="Mobile Number:"
                dataTestId="mobile-number"
                name="mobileNumber"
                className={cx(styles.primaryContact__formItem_vertical, styles.primaryContact__contactFormItem)}
                initialValue={initialValues?.mobile?.trim() || customer?.contact1.contact1_mobile?.trim()}
                hasUniqueNumberValidation
                isBusiness={isBusinessAccount}
                required={isMobileRequired}
              />
            </div>
            <div className={cx(styles.primaryContact__formRow, styles.primaryContact__formRow_reverse)}>
              <InputPhone.$
                label={
                  <Text.$>
                    Phone Number: <Text.$ type="secondary">(optional)</Text.$>
                  </Text.$>
                }
                dataTestId="phone-number"
                name="phoneNumber"
                className={cx(styles.primaryContact__formItem_vertical, styles.primaryContact__contactFormItem)}
                initialValue={customer?.phone?.trim()}
                required={false}
                spaces={[2, 6]}
              />
              <Select.$
                name="billTo"
                label={<Text.$>Bill to customer:</Text.$>}
                fullWidth
                initialValue={customer?.billto_ID === '' ? undefined : customer?.billto_ID}
                options={billToOptions}
                open={false}
                className={cx(styles.primaryContact__formItem_vertical, styles.primaryContact__contactFormItem)}
                dataTestId="bill-to-customer"
                placeholder="Select"
                suffixIcon={<SearchOutlined />}
                style={{ flexGrow: 0 }}
                onClick={() => {
                  !form?.getFieldValue('billTo') && setShowCustomerSearchModal(true);
                }}
                allowClear
              />
            </div>
            <div className={cx(styles.primaryContact__formRow, styles.primaryContact__formRow_alignTop)}>
              {form?.getFieldValue('businessNameOnly') ? (
                <InputText.$
                  name="companyName"
                  initialValue={customer?.company_name}
                  required
                  rules={[{ required: true }]}
                  label={<Text.$>Business Name:</Text.$>}
                  placeholder="Input"
                  className={cx(styles.primaryContact__formItem_vertical, styles.primaryContact__contactFormItem)}
                />
              ) : (
                <AbnLookup.$
                  form={form}
                  fieldsClassNames={cx(
                    styles.primaryContact__formItem_vertical,
                    styles.primaryContact__contactFormItem,
                  )}
                  disabled={!isBusinessAccount}
                  defaultAbn={customer?.abn}
                  defaultName={customer?.abn && customer?.company_name}
                />
              )}
              <Space.$ style={{ marginTop: 32 }}>
                <Switchbox.$
                  disabled={!isBusinessAccount}
                  initialValue={customer?.company_name && !customer?.abn}
                  name={'businessNameOnly'}
                  label="Business Name Only"
                />
              </Space.$>
            </div>
          </Space.$>
        </Space.$>

        <CustomerSearchModal.$
          customer={null}
          isOpen={showCustomerSearchModal}
          modalTitle={'Select Customer File'}
          actions={[
            {
              title: 'Select',
              action: (record: any) => {
                setBillToOptions([{ label: `${record.ID} - ${record.name.full_name}`, value: record.ID }]);
                form?.setFieldValue('billTo', record.ID);
                void validateFormSilently(form!, () => null);
                setShowCustomerSearchModal(false);
              },
            },
          ]}
          onOk={() => {
            setShowCustomerSearchModal(false);
          }}
          onCancel={() => {
            setShowCustomerSearchModal(false);
          }}
        />
      </Section.$>
    );
  };
}
